import { useCallback, useEffect, useRef } from "react";

import { Logger } from "@touchtech/web-logging";

import {
  LocalStorageKeys,
  SOCKET_MESSAGE_TYPE,
  WS_RECONNECT_LOG_MILESTONES,
} from "@is/constants";
import { LocalStorageService } from "@is/services";
import { getLiveBlobUrlCount } from "@is/utils";

import { webSocketWorker } from "../../workers";

type Snapshot = {
  liveBlobUrls?: number;
  heapMb?: number;
  videoCount?: number;
  imgCount?: number;
  wsCloseCode?: number;
  wsConnDurationSec?: number;
  appVersion?: string;
  pageUptimeSec?: number;
};

function readHeapMb(): number | undefined {
  try {
    const memory = (
      performance as unknown as { memory?: { usedJSHeapSize?: number } }
    ).memory;
    if (memory && typeof memory.usedJSHeapSize === "number") {
      return Math.round(memory.usedJSHeapSize / 1024 / 1024);
    }
  } catch {
    // perf.memory is Chrome-only; ignore failures elsewhere
  }
  return undefined;
}

function readDomCount(selector: string): number | undefined {
  try {
    if (typeof document === "undefined" || !document.querySelectorAll) {
      return undefined;
    }
    return document.querySelectorAll(selector).length;
  } catch {
    return undefined;
  }
}

function readAppVersion(): string | undefined {
  try {
    const value = LocalStorageService.getItem(LocalStorageKeys.Version);
    return typeof value === "string" && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

function readPageUptimeSec(): number | undefined {
  try {
    if (typeof performance === "undefined" || !performance.now) {
      return undefined;
    }
    const ms = performance.now();
    if (typeof ms === "number" && Number.isFinite(ms) && ms >= 0) {
      return Math.round(ms / 1000);
    }
  } catch {
    // never block telemetry
  }
  return undefined;
}

function buildSnapshot(
  closeCode: number | null,
  connDurationMs: number | null
): Snapshot {
  const snapshot: Snapshot = {};

  try {
    const blobs = getLiveBlobUrlCount();
    if (typeof blobs === "number") {
      snapshot.liveBlobUrls = blobs;
    }
  } catch {
    // never block telemetry
  }

  const heapMb = readHeapMb();
  if (typeof heapMb === "number") {
    snapshot.heapMb = heapMb;
  }

  const videoCount = readDomCount("video");
  if (typeof videoCount === "number") {
    snapshot.videoCount = videoCount;
  }

  const imgCount = readDomCount("img");
  if (typeof imgCount === "number") {
    snapshot.imgCount = imgCount;
  }

  if (typeof closeCode === "number") {
    snapshot.wsCloseCode = closeCode;
  }

  if (typeof connDurationMs === "number" && connDurationMs >= 0) {
    snapshot.wsConnDurationSec = Math.round(connDurationMs / 1000);
  }

  const appVersion = readAppVersion();
  if (appVersion) {
    snapshot.appVersion = appVersion;
  }

  const uptimeSec = readPageUptimeSec();
  if (typeof uptimeSec === "number") {
    snapshot.pageUptimeSec = uptimeSec;
  }

  return snapshot;
}

export function useWebSocketReconnectTelemetry() {
  const lastCloseCodeRef = useRef<number | null>(null);
  const lastConnDurationRef = useRef<number | null>(null);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (
      !event ||
      !event.data ||
      event.data.type !== SOCKET_MESSAGE_TYPE.RECONNECT
    ) {
      return;
    }

    const { reconnectCount, closeCode, connDurationMs } = event.data;

    if (typeof closeCode === "number") {
      lastCloseCodeRef.current = closeCode;
    }
    if (typeof connDurationMs === "number") {
      lastConnDurationRef.current = connDurationMs;
    }

    if (
      typeof reconnectCount !== "number" ||
      !WS_RECONNECT_LOG_MILESTONES.includes(reconnectCount)
    ) {
      return;
    }

    try {
      const snapshot = buildSnapshot(
        lastCloseCodeRef.current,
        lastConnDurationRef.current
      );

      Logger.instance.info("WebSocket reconnect milestone", {
        "tenant-id": LocalStorageService.getItem(LocalStorageKeys.TenantId),
        "device-id": LocalStorageService.getItem(LocalStorageKeys.DeviceId),
        "session-id": LocalStorageService.getItem(LocalStorageKeys.SessionId),
        reconnectCount,
        ...snapshot,
      });
    } catch {
      // telemetry should never break the app
    }
  }, []);

  useEffect(() => {
    webSocketWorker.addEventListener("message", handleMessage);

    return () => {
      webSocketWorker.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);
}
